/**
 * Rooms breakdown - one table per the de-slop board: room bucket,
 * ortalama AZN/m², month-over-month movement chip, active count. The
 * MoM chip reuses WoWBadge so movement keeps the app-wide semantic
 * scale (price up = rose, down = emerald); everything else stays
 * gray - the numbers do the talking.
 */

import WoWBadge from "@/components/trends/WoWBadge";

interface RoomRow {
  room_bucket: number;
  listing_count: number;
  median_ppm: number | null;
  /** Latest ppm vs ~4 ISO weeks earlier, from district-summary. */
  mom_ppm_pct?: number | null;
}

interface Props {
  rows: RoomRow[];
  /** Localized room label function passed in so this component stays
   *  i18n-agnostic (the caller has access to `t()`). */
  roomLabel: (bucket: number) => string;
  /** NBSP-formatted ppm string. */
  formatPpm: (n: number | null | undefined) => string;
  /** Localized column headers. */
  headers: { rooms: string; ppm: string; mom: string; count: string };
}

export default function RoomsGrid({ rows, roomLabel, formatPpm, headers }: Props) {
  return (
    <div className="rounded-2xl border border-gray-200 bg-white overflow-hidden">
      <div className="grid grid-cols-[1.2fr_1fr_0.9fr_0.8fr] border-b border-gray-100 bg-gray-50">
        {[headers.rooms, headers.ppm, headers.mom, headers.count].map((h, i) => (
          <span
            key={h}
            className={`px-4 sm:px-5 py-2.5 text-[11px] font-bold text-gray-500 ${i > 0 ? "text-right" : ""}`}
          >
            {h}
          </span>
        ))}
      </div>
      {rows.map((r) => (
        <div
          key={r.room_bucket}
          className="grid grid-cols-[1.2fr_1fr_0.9fr_0.8fr] items-center border-b border-gray-100 last:border-b-0"
        >
          <span className="px-4 sm:px-5 py-3 text-sm font-bold text-gray-900">
            {roomLabel(r.room_bucket)}
          </span>
          <span className="px-4 sm:px-5 py-3 text-right text-sm text-gray-900 tabular-nums">
            {formatPpm(r.median_ppm)}
          </span>
          <span className="px-4 sm:px-5 py-3 flex justify-end">
            <WoWBadge wow={r.mom_ppm_pct} size="sm" />
          </span>
          <span className="px-4 sm:px-5 py-3 text-right text-sm text-gray-500 tabular-nums">
            {r.listing_count}
          </span>
        </div>
      ))}
    </div>
  );
}
